import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { FornitoriAdmin } from 'src/app/models/supplier-portal/supplier/fornitori-admin.models';
import { FornitoriAdminMainService } from 'src/app/services/fornitori-admin-main.service';

type CascadeCol = 'product_code' | 'order_number' | 'lotto' | 'serial';

export interface SerialRange {
  from: number;
  to: number;
  qty: number;
  key: string;
}

@Component({
  selector: 'app-tracciability-filter-modal',
  standalone: false,
  templateUrl: './tracciability-filter-modal.component.html',
  styleUrls: ['./tracciability-filter-modal.component.scss'],
})
export class TracciabilityFilterModalComponent implements OnChanges {
  @Input() open = false;
  @Input() results: FornitoriAdmin.TraceSearchResult[] = [];
  @Input() dateFrom = '';
  @Input() dateTo = '';
  @Output() closeModal = new EventEmitter<void>();
  @Output() openDetail = new EventEmitter<FornitoriAdmin.TraceSearchResult>();

  public filteredResults: FornitoriAdmin.TraceSearchResult[] = [];
  public activeDropdown: CascadeCol | null = null;
  public panelPos = { top: 0, left: 0 };

  // Cascade selections
  public selectedArt: string | null = null;
  public selectedFor: string | null = null;
  public selectedLot: FornitoriAdmin.TraceBatch | null = null;
  public selectedSerial: SerialRange | null = null;

  // Cascade data loaded from API
  public products: FornitoriAdmin.TraceProduct[] = [];
  public suppliers: FornitoriAdmin.TraceSupplier[] = [];
  public lots: FornitoriAdmin.TraceBatch[] = [];
  public serials: SerialRange[] = [];

  // Per-dropdown loading flags
  public loadingProducts = false;
  public loadingSuppliers = false;
  public loadingLots = false;
  public loadingSerials = false;

  public loadingResults = false;

  constructor(
    private readonly svc: FornitoriAdminMainService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(): void {
    if (!this.open) {
      this.resetAll();
      //this.filteredResults = [];
    }
  }

  // ── Dropdown toggle ───────────────────────────────────────────────────────

  toggleDropdown(col: CascadeCol, event: Event): void {
    event.stopPropagation();
    if (this.isDisabled(col)) return;

    if (this.activeDropdown === col) {
      this.activeDropdown = null;
      return;
    }

    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.panelPos = {
      top: rect.bottom + 4,
      left: rect.left + rect.width / 2,
    };

    this.activeDropdown = col;

    switch (col) {
      case 'product_code':
        if (this.products.length === 0) this.loadProducts();
        break;
      case 'order_number':
        if (this.suppliers.length === 0) this.loadSuppliers();
        break;
      case 'lotto':
        if (this.lots.length === 0) this.loadLots();
        break;
      case 'serial':
        if (this.serials.length === 0) this.loadSerials();
        break;
    }
  }

  // ── API loaders ───────────────────────────────────────────────────────────

  private loadProducts(): void {
    this.loadingProducts = true;
    this.svc.getTraceProducts().subscribe({
      next: (data) => {
        this.products = data;
        this.loadingProducts = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingProducts = false;
        this.cdr.detectChanges();
      },
    });
  }

  private loadSuppliers(): void {
    if (!this.selectedArt) return;
    this.loadingSuppliers = true;
    this.svc.getTraceSuppliers(this.selectedArt).subscribe({
      next: (data) => {
        this.suppliers = data;
        this.loadingSuppliers = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingSuppliers = false;
        this.cdr.detectChanges();
      },
    });
  }

  private loadLots(): void {
    if (!this.selectedArt || !this.selectedFor) return;
    this.loadingLots = true;
    this.svc.getTraceLots(this.selectedArt, this.selectedFor).subscribe({
      next: (data) => {
        this.lots = data;
        this.loadingLots = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingLots = false;
        this.cdr.detectChanges();
      },
    });
  }

  private loadSerials(): void {
    if (!this.selectedArt || !this.selectedFor || !this.selectedLot) return;
    this.loadingSerials = true;
    this.svc
      .getTraceSerials(this.selectedArt, this.selectedFor, this.selectedLot.lot)
      .subscribe({
        next: (data) => {
          this.serials = data.map((s) => ({
            from: s.fcp_seriale_da,
            to: s.fcp_seriale_a,
            qty: s.fcp_qta,
            key: `${s.fcp_seriale_da}-${s.fcp_seriale_a}`,
          }));
          this.loadingSerials = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loadingSerials = false;
          this.cdr.detectChanges();
        },
      });
  }

  // ── Selection handlers ────────────────────────────────────────────────────

  selectArt(code: string): void {
    if (this.selectedArt === code) {
      this.resetAll();
      //this.filteredResults = [];
      this.cdr.detectChanges();
      return;
    }
    this.selectedArt = code;
    this.selectedFor = null;
    this.selectedLot = null;
    this.selectedSerial = null;
    this.suppliers = [];
    this.lots = [];
    this.serials = [];
    this.activeDropdown = null;
    this.cdr.detectChanges();
    this.searchWithCurrentFilters();
  }

  selectFor(code: string): void {
    this.selectedLot = null;
    this.selectedSerial = null;
    this.lots = [];
    this.serials = [];
    this.activeDropdown = null;
    this.selectedFor = this.selectedFor === code ? null : code;
    this.cdr.detectChanges();
    this.searchWithCurrentFilters();
  }

  selectLot(batch: FornitoriAdmin.TraceBatch): void {
    this.selectedSerial = null;
    this.serials = [];
    this.activeDropdown = null;
    this.selectedLot = this.selectedLot?.lot === batch.lot ? null : batch;
    this.cdr.detectChanges();
    this.searchWithCurrentFilters();
  }

  selectSerial(range: SerialRange): void {
    this.activeDropdown = null;
    this.selectedSerial = this.selectedSerial?.key === range.key ? null : range;
    this.cdr.detectChanges();
    this.searchWithCurrentFilters();
  }

  clearAll(): void {
    this.resetAll();
    this.filteredResults = [];
  }

  // ── State helpers ─────────────────────────────────────────────────────────

  isActive(col: CascadeCol): boolean {
    switch (col) {
      case 'product_code':
        return !!this.selectedArt;
      case 'order_number':
        return !!this.selectedFor;
      case 'lotto':
        return !!this.selectedLot;
      case 'serial':
        return !!this.selectedSerial;
    }
  }

  isDisabled(col: CascadeCol): boolean {
    switch (col) {
      case 'product_code':
        return false;
      case 'order_number':
        return !this.selectedArt;
      case 'lotto':
        return !this.selectedFor;
      case 'serial':
        return !this.selectedLot;
    }
  }

  panelMeta(): { title: string; icon: string } {
    switch (this.activeDropdown) {
      case 'product_code':
        return { title: 'ARTICOLI', icon: 'bx bxs-shopping-bag' };
      case 'order_number':
        return { title: 'FORNITORI', icon: 'bx bxs-user' };
      case 'lotto':
        return { title: 'LOTTI', icon: 'bx bx-package' };
      case 'serial':
        return { title: 'SERIALI', icon: 'bx bx-barcode' };
      default:
        return { title: '', icon: '' };
    }
  }

  hasAnyFilter(): boolean {
    return !!(
      this.selectedArt ||
      this.selectedFor ||
      this.selectedLot ||
      this.selectedSerial
    );
  }

  closeDropdowns(): void {
    this.activeDropdown = null;
  }

  formatLot(year: number, week: number): string {
    if (!year && !week) return '-';
    return `${year}/${String(week).padStart(2, '0')}`;
  }

  lotLabel(batch: FornitoriAdmin.TraceBatch): string {
    return this.formatLot(batch.fcp_anno, batch.fcp_settimana);
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private resetAll(): void {
    this.selectedArt = null;
    this.selectedFor = null;
    this.selectedLot = null;
    this.selectedSerial = null;
    this.products = [];
    this.suppliers = [];
    this.lots = [];
    this.serials = [];
    this.activeDropdown = null;
  }

  private buildQr(): string {
    const parts: string[] = [];

    if (this.selectedArt) {
      parts.push(this.selectedArt);
    }

    if (this.selectedFor) {
      parts.push(this.selectedFor);
    }

    if (this.selectedLot) {
      const yy = String(this.selectedLot.fcp_anno).slice(-2);
      const ww = String(this.selectedLot.fcp_settimana).padStart(2, '0');
      let lotPart = `${ww}${yy}`;
      if (this.selectedSerial) {
        lotPart += `-${String(this.selectedSerial.from).padStart(4, '0')}`;
      }
      parts.push(lotPart);
    }
    return parts.join('$');
  }

  private searchWithCurrentFilters(): void {
    if (!this.selectedArt) {
      this.filteredResults = [];
      return;
    }

    const input = new FornitoriAdmin.TraceSearchInput();
    if (this.selectedArt) {
      input.art_code = this.selectedArt;
    }
    const qr = this.buildQr();
    if (qr) {
      input.art_qr = qr;
    }
    input.date_from = this.dateFrom || '';
    input.date_to = this.dateTo || '';
    this.loadingResults = true;

    this.svc.searchTraceProducts(input).subscribe({
      next: (data) => {
        this.filteredResults = [...data];
        this.loadingResults = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingResults = false;
        this.cdr.detectChanges();
      },
    });
  }
}
